import { m } from 'framer-motion';
// @mui
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
// routes
import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
// auth
import { useAuthContext } from 'src/auth/hooks'
// api
import { logoutCashRegister } from 'src/api/server/user'
// components
import { varHover } from 'src/components/animate';
import { useSnackbar } from 'src/components/snackbar';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import Iconify from 'src/components/iconify';
import { useCashRegisterSession } from 'src/hooks/use-cash-register-session';
import { useLocales } from 'src/locales';
import { FCurrency } from 'src/utils/format-number';
import { useEffect, useState, useCallback } from 'react';

// ----------------------------------------------------------------------

const OPTIONS = [
  {
    label: 'Home',
    linkTo: '/',
  },
  {
    label: 'Account',
    linkTo: paths.dashboard.user.account,
  }
]

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const router = useRouter();
  const { t } = useLocales();
  const { logout, user } = useAuthContext();
  const { session, isCashier, handleCloseSessionOnly } = useCashRegisterSession();

  useEffect(() => {
    if (user?.role === 'Admin') OPTIONS.push({ label: 'Configuration', linkTo: paths.dashboard.user.config })
  }, [user?.role]);

  const { enqueueSnackbar } = useSnackbar();
  const popover = usePopover();

  // State for the "close session before logout?" confirmation dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  // Track what action to take after closing the session
  const [pendingAction, setPendingAction] = useState<'logout' | 'change_register' | null>(null);
  // State for the inline close session dialog (shown inside confirm dialog)
  const [closingAmount, setClosingAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Compute expected amount for the close dialog
  const expectedAmount = (() => {
    if (!session) return 0;
    if (session.currentAmount !== undefined && session.currentAmount !== null) return session.currentAmount;
    if (session.expectedAmount !== undefined && session.expectedAmount !== null) return session.expectedAmount;
    const transactions = session.transactions || [];
    const totalSales = transactions
      .filter((t: any) => t.type === 'sale' || t.type === 'deposit')
      .reduce((sum: number, t: any) => sum + t.amount, 0);
    const totalExpenses = transactions
      .filter((t: any) => t.type === 'withdrawal' || t.type === 'refund')
      .reduce((sum: number, t: any) => sum + t.amount, 0);
    return (session.openingAmount || 0) + totalSales - totalExpenses;
  })();

  // Check if there's an active session that needs closing
  const hasActiveSession = isCashier && session && session.status === 'open';

  const executeLogout = useCallback(async () => {
    try {
      if (user) {
        try {
          await logoutCashRegister(user.id);
        } catch (cashRegisterError) {
          console.warn('Cash register logout failed:', cashRegisterError);
        }
      }
      await logout();
      popover.onClose();
      router.replace('/');
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  }, [user, logout, popover, router, enqueueSnackbar]);

  const executeChangeRegister = useCallback(async () => {
    try {
      if (user) {
        await logoutCashRegister(user.id);
        popover.onClose();
        router.reload();
      }
    } catch (error) {
      console.error('Cash register logout error:', error);
      if (error?.error?.includes('Cash register not found') || error?.message?.includes('Cash register not found')) {
        enqueueSnackbar('No active cash register session found', { variant: 'warning' });
      } else {
        enqueueSnackbar('Unable to logout cash register', { variant: 'error' });
      }
      popover.onClose();
    }
  }, [user, popover, router, enqueueSnackbar]);

  const handleLogout = async () => {
    if (hasActiveSession) {
      setPendingAction('logout');
      setConfirmDialogOpen(true);
      return;
    }
    await executeLogout();
  };

  const handleLogoutCashRegister = async () => {
    if (hasActiveSession) {
      setPendingAction('change_register');
      setConfirmDialogOpen(true);
      return;
    }
    await executeChangeRegister();
  };

  const handleConfirmCloseSession = async () => {
    const amount = parseFloat(closingAmount);
    if (Number.isNaN(amount) || amount < 0) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      await handleCloseSessionOnly(amount);
      setConfirmDialogOpen(false);
      setClosingAmount('');
      setSubmitError(null);

      // Execute the pending action after successful close
      if (pendingAction === 'logout') {
        await executeLogout();
      } else if (pendingAction === 'change_register') {
        await executeChangeRegister();
      }
      setPendingAction(null);
    } catch (err: any) {
      setSubmitError(err.message || t('cash_register.errors.session_close_error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkipCloseSession = async () => {
    setConfirmDialogOpen(false);
    setClosingAmount('');
    setSubmitError(null);

    if (pendingAction === 'logout') {
      await executeLogout();
    } else if (pendingAction === 'change_register') {
      await executeChangeRegister();
    }
    setPendingAction(null);
  };

  const handleCancelConfirm = () => {
    setConfirmDialogOpen(false);
    setPendingAction(null);
    setClosingAmount('');
    setSubmitError(null);
  };

  const handleClickItem = (path: string) => {
    popover.onClose();
    router.push(path);
  };

  return (
    <>
      <m.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }} variants={varHover(1.05)}>
        <IconButton
          onClick={popover.onOpen}
          sx={{
            width: 40,
            height: 40,
            background: (theme) => alpha(theme.palette.grey[500], 0.08),
            ...(popover.open && {
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
            }),
          }}
        >
        <Avatar
          src={user?.coverImage}
          alt={user?.displayName}
          sx={{
            width: 36,
            height: 36,
            border: (theme) => `solid 2px ${theme.palette.background.default}`,
          }}
        />
        </IconButton>
      </m.div>

      <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ width: 200, p: 0 }}>
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography variant="subtitle2" noWrap>
            {user?.displayName}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          {OPTIONS.map((option) => (
            <MenuItem key={option.label} onClick={() => handleClickItem(option.linkTo)}>
              {option.label}
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem
          onClick={handleLogout}
          sx={{ m: 1, fontWeight: 'fontWeightBold', color: 'error.main' }}
        >
          Logout
        </MenuItem>

        <MenuItem
          onClick={handleLogoutCashRegister}
          sx={{ m: 1, fontWeight: 'fontWeightBold', color: 'error.main' }}
        >
          Change cash register
        </MenuItem>
      </CustomPopover>

      {/* Confirmation dialog: "Close session before logout?" */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelConfirm}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Iconify icon="eva:alert-circle-fill" width={24} color="#00A76F" />
            <Typography variant="h6">
              {t('cash_register.close_session_title') || 'Fermer la session de caisse'}
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('cash_register.close_session_prompt') || 'Une session de caisse est actuellement ouverte. Souhaitez-vous la fermer avant de continuer ?'}
          </Typography>

          {/* Session summary */}
          {session && (
            <Stack
              spacing={1}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              }}
            >
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t('cash_register.opening_amount')}
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {FCurrency(session.openingAmount)}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {t('cash_register.expected_amount')}
                </Typography>
                <Typography variant="body2" fontWeight="bold" color="primary.main">
                  {FCurrency(expectedAmount)}
                </Typography>
              </Stack>
            </Stack>
          )}

          {/* Closing amount input */}
          <Typography variant="subtitle2" fontWeight="medium" sx={{ mt: 2, mb: 1 }}>
            {t('cash_register.dialogs.close.amount_label') || 'Montant de fermeture'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Typography variant="body1" sx={{ pt: 1.5, fontWeight: 'bold' }}>€</Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              value={closingAmount}
              onChange={(e) => setClosingAmount(e.target.value)}
              placeholder={expectedAmount.toString()}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: 2 },
              }}
            />
          </Box>

          {submitError && (
            <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>
              {submitError}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={handleCancelConfirm} disabled={submitting} sx={{ borderRadius: 2 }}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSkipCloseSession}
            disabled={submitting}
            color="inherit"
            sx={{ borderRadius: 2 }}
          >
            {t('cash_register.skip_close') || 'Ignorer et continuer'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmCloseSession}
            disabled={submitting || !closingAmount || parseFloat(closingAmount) < 0}
            sx={{ borderRadius: 2 }}
          >
            {t('cash_register.close_and_continue_action') || 'Fermer et continuer'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
